import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiUrl } from '../config/environment';

/**
 * PublicBooking — unauthenticated self-booking wizard.
 *
 * Routed via the early-return pattern in App.tsx when the path starts with
 * `/book/`. URL contract:  /book/:tenantSlug
 *
 * Flexible flow (identify-first):
 *   identify → the server matches the phone/email against existing patients:
 *     • EXISTING patient  → books a "consulta" (no procedure choice, no payment)
 *     • NEW patient       → books an "avaliação"; if the clinic requires it, must
 *                           pay in advance (PIX/card) before the booking confirms.
 *
 * No Sidebar, no auth, no app chrome. Talks only to /api/public/booking/:slug.
 */

interface Clinic { name: string; slug: string; timeZone: string; }
interface Professional { id: number; name: string; }
interface Slot { professionalId: number; professionalName: string; start: string; end: string; startUtc: string; endUtc: string; }
interface ResolvedProcedure { id: number; name: string; durationMinutes: number; }
interface PaymentInfo { paymentId: string; status: string; checkoutUrl: string | null; pixQrCode: string | null; pixCopyPaste: string | null; expiresAt: string | null; }

type Step = 'identify' | 'datetime' | 'details' | 'payment' | 'done' | 'notfound';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const PublicBooking: React.FC = () => {
  const { t } = useTranslation();
  const slug = (() => {
    const m = window.location.pathname.match(/^\/book\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  })();
  // Current recall links carry a server-verified `token` (see
  // recall_discount_grants) instead of a bare percentage. `discount` is kept
  // only as a fallback for links already delivered before the token-based
  // flow shipped.
  const recallDiscountToken = new URLSearchParams(window.location.search).get('token') || null;
  const recallDiscount = (() => {
    if (recallDiscountToken) return null;
    const v = new URLSearchParams(window.location.search).get('discount');
    const n = v ? parseFloat(v) : NaN;
    return !isNaN(n) && n > 0 && n <= 100 ? n : null;
  })();

  const [step, setStep] = useState<Step>('identify');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [professionals, setProfessionals] = useState<Professional[]>([]);

  // Identity + resolved flow.
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [identifying, setIdentifying] = useState(false);
  const [flow, setFlow] = useState<'existing' | 'new' | null>(null);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [priceCents, setPriceCents] = useState<number | null>(null);
  const [procedure, setProcedure] = useState<ResolvedProcedure | null>(null);

  const [date, setDate] = useState<string>(todayISO());
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  // Calendar: track which month is displayed and which dates have slots.
  const [viewMonth, setViewMonth] = useState<string>(todayISO().slice(0, 7)); // "YYYY-MM"
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [calLoading, setCalLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<{ start: string; date: string } | null>(null);

  // Payment.
  const [payment, setPayment] = useState<PaymentInfo | null>(null);
  const [paying, setPaying] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fmtPrice = (cents: number | null) =>
    cents == null ? '' : (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // ── Load clinic + professionals (validates the slug) ───────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}`));
        if (res.status === 404) { if (!cancelled) { setStep('notfound'); setLoading(false); } return; }
        const json = await res.json();
        if (!json.success) { if (!cancelled) { setStep('notfound'); setLoading(false); } return; }
        if (cancelled) return;
        setClinic(json.data.clinic);
        setProfessionals(json.data.professionals || []);
        setLoading(false);
      } catch {
        if (!cancelled) { setError(t('booking.loadError', 'Could not load this page. Please try again.')); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Identify the patient and resolve the flow ──────────────────────────────
  const identify = async () => {
    if (phone.trim().length < 6) return;
    setIdentifying(true);
    setError(null);
    try {
      const res = await fetch(resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}/identify`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), email: email.trim() || undefined }),
      });
      if (res.status === 404) { setStep('notfound'); return; }
      if (res.status === 429) { setError(t('booking.tooMany', 'Too many requests. Please try again later.')); return; }
      const json = await res.json();
      if (res.status === 409 || !json.success) {
        setError(t('booking.unavailable', 'Online booking is currently unavailable. Please contact the clinic.'));
        return;
      }
      setFlow(json.data.flow);
      setRequiresPayment(!!json.data.requiresPayment);
      setPriceCents(json.data.priceCents ?? null);
      setProcedure(json.data.procedure);
      setStep('datetime');
    } catch {
      setError(t('booking.loadError', 'Could not load this page. Please try again.'));
    } finally {
      setIdentifying(false);
    }
  };

  // ── Load slots whenever procedure/date changes on the datetime step ───────
  const loadSlots = useCallback(async () => {
    if (!procedure || !date) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const url = resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}/availability?date=${date}&procedureId=${procedure.id}`);
      const res = await fetch(url);
      const json = await res.json();
      setSlots(json.success ? (json.data.slots || []) : []);
    } catch {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [slug, procedure, date]);

  useEffect(() => {
    if (step === 'datetime') loadSlots();
  }, [step, loadSlots]);

  // Fetch which dates in viewMonth have available slots.
  useEffect(() => {
    if (!procedure || step !== 'datetime') return;
    let cancelled = false;
    setCalLoading(true);
    (async () => {
      try {
        const url = resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}/available-dates?month=${viewMonth}&procedureId=${procedure.id}`);
        const res = await fetch(url);
        const json = await res.json();
        if (!cancelled) setAvailableDates(new Set(json.success ? (json.data.availableDates || []) : []));
      } catch {
        if (!cancelled) setAvailableDates(new Set());
      } finally {
        if (!cancelled) setCalLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, procedure, viewMonth, step]); // eslint-disable-line react-hooks/exhaustive-deps

  // When selected date moves to a different month (e.g. booking started with today),
  // sync viewMonth so the calendar shows the right month.
  useEffect(() => {
    const m = date.slice(0, 7);
    if (m !== viewMonth) setViewMonth(m);
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Submit booking ────────────────────────────────────────────────────────
  const submit = async () => {
    if (!selectedSlot || !procedure) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}/book`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          professionalId: selectedSlot.professionalId,
          startUtc: selectedSlot.startUtc,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          notes: notes.trim() || undefined,
          ...(recallDiscountToken != null && { discountToken: recallDiscountToken }),
          ...(recallDiscount != null && { discountPercentage: recallDiscount }),
        }),
      });
      const json = await res.json();
      if (res.status === 409) {
        setError(t('booking.slotTaken', 'That time was just taken. Please pick another.'));
        setStep('datetime');
        loadSlots();
        return;
      }
      if (res.status === 429) {
        setError(t('booking.tooMany', 'Too many bookings. Please contact the clinic directly.'));
        return;
      }
      if (!json.success) {
        setError(t('booking.bookError', 'Could not complete the booking. Please try again.'));
        return;
      }
      if (json.data.flow === 'new' && json.data.payment) {
        setPayment(json.data.payment);
        setStep('payment');
        return;
      }
      setConfirmation({ start: selectedSlot.start, date });
      setStep('done');
    } catch {
      setError(t('booking.bookError', 'Could not complete the booking. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment: poll for settlement + countdown ───────────────────────────────
  const checkPayment = useCallback(async (): Promise<boolean> => {
    if (!payment) return false;
    try {
      const res = await fetch(resolveApiUrl(`/api/public/booking/${encodeURIComponent(slug)}/payments/${encodeURIComponent(payment.paymentId)}/confirm`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (json.success && json.data.status === 'paid') {
        setConfirmation({ start: selectedSlot?.start || '', date });
        setStep('done');
        return true;
      }
    } catch { /* keep polling */ }
    return false;
  }, [payment, slug, selectedSlot, date]);

  useEffect(() => {
    if (step !== 'payment' || !payment) return;
    // Poll every 5s for settlement.
    pollRef.current = setInterval(() => { checkPayment(); }, 5000);
    // Countdown to expiry.
    const tick = setInterval(() => {
      if (!payment.expiresAt) { setSecondsLeft(null); return; }
      const left = Math.round((new Date(payment.expiresAt).getTime() - Date.now()) / 1000);
      setSecondsLeft(left);
      if (left <= 0) {
        setError(t('booking.paymentExpired', 'Payment time expired. Please start the booking again.'));
        setStep('identify');
        setPayment(null);
      }
    }, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      clearInterval(tick);
    };
  }, [step, payment, checkPayment]); // eslint-disable-line react-hooks/exhaustive-deps

  const payNow = async () => {
    setPaying(true);
    try { await checkPayment(); } finally { setPaying(false); }
  };

  const copyPix = async () => {
    if (!payment?.pixCopyPaste) return;
    try {
      await navigator.clipboard.writeText(payment.pixCopyPaste);
      setPixCopied(true);
      setTimeout(() => setPixCopied(false), 1500);
    } catch { /* clipboard may be blocked on non-HTTPS */ }
  };

  // ── Inline booking calendar ───────────────────────────────────────────────
  const renderCalendar = () => {
    const today = todayISO();
    const [vy, vm] = viewMonth.split('-').map(Number);
    const firstWeekday = new Date(vy, vm - 1, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(vy, vm, 0).getDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    const monthLabel = new Date(vy, vm - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

    const prevMonth = () => {
      const d = new Date(vy, vm - 2, 1);
      setViewMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
    };
    const nextMonth = () => {
      const d = new Date(vy, vm, 1);
      setViewMonth(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
    };

    const cells: React.ReactNode[] = [];
    // Leading empty cells
    for (let i = 0; i < firstWeekday; i++) cells.push(<div key={`e${i}`} />);

    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${vy}-${pad(vm)}-${pad(d)}`;
      const isAvailable = availableDates.has(ds);
      const isSelected = ds === date;
      const isToday = ds === today;
      const isPast = ds < today;
      const disabled = isPast || !isAvailable;

      const bg = isSelected ? 'var(--accent)' : isToday && !isSelected ? 'var(--accent-faint, #e8f5f0)' : 'transparent';
      const color = isSelected ? '#fff' : disabled ? 'var(--ink-faint, #ccc)' : 'var(--ink, #111)';

      cells.push(
        <button key={ds} type="button" disabled={disabled} onClick={() => setDate(ds)}
          style={{
            width: '100%', aspectRatio: '1', border: 'none', borderRadius: '50%',
            background: bg, color, fontWeight: isSelected || isToday ? 700 : 400,
            fontSize: 13, cursor: disabled ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            textDecoration: disabled && !isPast ? 'none' : undefined,
          }}>
          {d}
        </button>
      );
    }

    return (
      <div style={{ marginBottom: 14, userSelect: 'none' }}>
        {/* Month header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <button type="button" onClick={prevMonth} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px', color: 'var(--ink-muted)' }}>‹</button>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{monthLabel}</span>
          <button type="button" onClick={nextMonth} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px', color: 'var(--ink-muted)' }}>›</button>
        </div>
        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--ink-muted)', padding: '2px 0' }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, opacity: calLoading ? 0.4 : 1, transition: 'opacity 0.15s' }}>
          {cells}
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const shell = (children: React.ReactNode) => (
    <div className="pb-shell">
      <div style={{ width: '100%', maxWidth: 520 }}>
        {clinic && (
          <div style={{ marginBottom: 20, textAlign: 'center' }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink, #111)', margin: 0 }}>{clinic.name}</h1>
            <div style={{ fontSize: 13, color: 'var(--ink-muted, #666)', marginTop: 4 }}>{t('booking.subtitle', 'Schedule your appointment')}</div>
          </div>
        )}
        <div style={{ background: 'var(--surface, #fff)', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 20 }}>
          {children}
        </div>
      </div>
    </div>
  );

  if (loading) return shell(<div style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>{t('common.loading', 'Loading…')}</div>);

  if (step === 'notfound') {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>🔍</div>
        <h2 style={{ fontSize: 18 }}>{t('booking.notFound', 'Page not found')}</h2>
        <p style={{ color: 'var(--ink-muted)', fontSize: 14 }}>{t('booking.notFoundDesc', 'This booking link is not available.')}</p>
      </div>
    );
  }

  const btn: React.CSSProperties = { width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer' };
  const btnGhost: React.CSSProperties = { ...btn, background: 'transparent', color: 'var(--ink-muted)', border: '1px solid var(--line, #ddd)' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--line, #ddd)', fontSize: 14, boxSizing: 'border-box' };

  return shell(
    <>
      {error && <div role="alert" style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {recallDiscount && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 14, color: '#166534', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          🎁 {t('booking.recallDiscount', '{{pct}}% de desconto especial neste agendamento!', { pct: recallDiscount })}
        </div>
      )}

      {step === 'identify' && (
        <div>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('booking.identifyTitle', 'Vamos começar')}</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: 13, marginTop: 0 }}>
            {t('booking.identifyDesc', 'Informe seu celular para agendar. Se você já é paciente, marcamos sua consulta direto.')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input style={inputStyle} placeholder={t('booking.phone', 'WhatsApp phone') as string} value={phone} onChange={e => setPhone(e.target.value)} inputMode="tel" autoFocus />
            <input style={inputStyle} placeholder={t('booking.emailOptional', 'E-mail (opcional)') as string} value={email} onChange={e => setEmail(e.target.value)} inputMode="email" />
          </div>
          <button type="button" disabled={identifying || phone.trim().length < 6} onClick={identify} style={{ ...btn, marginTop: 16, opacity: (identifying || phone.trim().length < 6) ? 0.6 : 1 }}>
            {identifying ? t('common.loading', 'Loading…') : t('common.continue', 'Continue')}
          </button>
        </div>
      )}

      {step === 'datetime' && procedure && (
        <div>
          <button type="button" onClick={() => setStep('identify')} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', padding: 0, fontSize: 13, marginBottom: 8 }}>‹ {t('common.back', 'Back')}</button>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>{procedure.name}</h2>
          {flow === 'new' && requiresPayment && priceCents != null && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e40af' }}>
              {t('booking.evalNotice', 'Avaliação para novos pacientes — {{price}} pagos no agendamento.', { price: fmtPrice(priceCents) })}
            </div>
          )}
          {renderCalendar()}
          {slotsLoading ? (
            <div style={{ color: 'var(--ink-muted)', fontSize: 14 }}>{t('booking.loadingSlots', 'Finding available times…')}</div>
          ) : slots.length === 0 ? (
            <div style={{ color: 'var(--ink-muted)', fontSize: 14 }}>{t('booking.noSlots', 'No times available on this day.')}</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
              {slots.map((s, i) => {
                const active = selectedSlot?.startUtc === s.startUtc && selectedSlot?.professionalId === s.professionalId;
                return (
                  <button key={i} type="button" onClick={() => setSelectedSlot(s)}
                    title={professionals.length > 1 ? s.professionalName : undefined}
                    style={{ padding: '10px 4px', borderRadius: 8, border: `1px solid ${active ? 'var(--accent)' : 'var(--line, #ddd)'}`, background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--ink)', cursor: 'pointer', fontSize: 14 }}>
                    {s.start}
                    {professionals.length > 1 && <span style={{ display: 'block', fontSize: 10, opacity: 0.8 }}>{s.professionalName}</span>}
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" disabled={!selectedSlot} onClick={() => setStep('details')} style={{ ...btn, marginTop: 16, opacity: selectedSlot ? 1 : 0.5 }}>
            {t('common.continue', 'Continue')}
          </button>
        </div>
      )}

      {step === 'details' && procedure && (
        <div>
          <button type="button" onClick={() => setStep('datetime')} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', padding: 0, fontSize: 13, marginBottom: 8 }}>‹ {t('common.back', 'Back')}</button>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('booking.yourDetails', 'Your details')}</h2>
          <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 14 }}>
            {procedure.name} · {date} · {selectedSlot?.start}
            {flow === 'new' && requiresPayment && priceCents != null && ` · ${fmtPrice(priceCents)}`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input style={inputStyle} placeholder={t('booking.name', 'Full name') as string} value={name} onChange={e => setName(e.target.value)} autoFocus />
            <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} placeholder={t('booking.notes', 'Notes (optional)') as string} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <button type="button" disabled={submitting || name.trim().length < 2}
            onClick={submit} style={{ ...btn, marginTop: 16, opacity: submitting ? 0.6 : 1 }}>
            {submitting ? t('booking.booking', 'Booking…')
              : (flow === 'new' && requiresPayment ? t('booking.continueToPayment', 'Ir para pagamento') : t('booking.confirm', 'Confirm booking'))}
          </button>
        </div>
      )}

      {step === 'payment' && payment && (
        <div>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>{t('booking.payTitle', 'Pagamento da avaliação')}</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: 13, marginTop: 0 }}>
            {t('booking.payDesc', 'Pague para confirmar seu horário.')}
            {priceCents != null && <strong style={{ color: 'var(--ink)' }}> {fmtPrice(priceCents)}</strong>}
          </p>
          {secondsLeft != null && secondsLeft > 0 && (
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginBottom: 12 }}>
              {t('booking.payCountdown', 'Tempo restante: {{m}}:{{s}}', { m: Math.floor(secondsLeft / 60), s: String(secondsLeft % 60).padStart(2, '0') })}
            </div>
          )}
          {payment.pixQrCode && (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <img src={payment.pixQrCode} alt="PIX QR code" style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid var(--line, #eee)' }} />
            </div>
          )}
          {payment.pixCopyPaste && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: 'var(--ink-muted)', display: 'block', marginBottom: 4 }}>{t('booking.payPix', 'PIX copia e cola')}</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{ flex: 1, background: 'var(--bg, #f3f4f6)', padding: '8px 10px', borderRadius: 6, fontSize: 12, wordBreak: 'break-all' }}>{payment.pixCopyPaste}</code>
                <button type="button" onClick={copyPix} style={{ ...btnGhost, width: 'auto', padding: '8px 12px', fontSize: 13 }}>
                  {pixCopied ? t('common.copied', 'Copiado!') : t('common.copy', 'Copiar')}
                </button>
              </div>
            </div>
          )}
          {payment.checkoutUrl && (
            <a href={payment.checkoutUrl} target="_blank" rel="noopener noreferrer" style={{ ...btn, display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 10 }}>
              {t('booking.payCard', 'Pagar com cartão')}
            </a>
          )}
          <button type="button" onClick={payNow} disabled={paying} style={{ ...btnGhost, opacity: paying ? 0.6 : 1 }}>
            {paying ? t('booking.checking', 'Verificando…') : t('booking.alreadyPaid', 'Já paguei')}
          </button>
          <p style={{ color: 'var(--ink-faint)', fontSize: 12, marginTop: 10, textAlign: 'center' }}>
            {t('booking.payAutoConfirm', 'Confirmamos automaticamente assim que o pagamento for identificado.')}
          </p>
        </div>
      )}

      {step === 'done' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44 }}>✅</div>
          <h2 style={{ fontSize: 18 }}>{t('booking.confirmedTitle', 'Booking confirmed!')}</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: 14 }}>
            {t('booking.confirmedDesc', 'We will send a confirmation to your WhatsApp.')}
          </p>
          {confirmation && <div style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>{confirmation.date} · {confirmation.start}</div>}
        </div>
      )}
    </>
  );
};

export default PublicBooking;
